import React, { useCallback, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import {
  FaBan,
  FaCheckCircle,
  FaClipboardCheck,
  FaDolly,
  FaExclamationTriangle,
  FaPlus,
  FaShareSquare,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import {
  apiError,
  dateText,
  fetchPaged,
  FilterOption,
  fullName,
  HolderType,
  materialFilters,
  materialMasters,
  num,
  qty,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  DetailRow,
  ExpandAction,
  Field,
  FilterField,
  FilterRow,
  GhostButton,
  HolderChip,
  HolderPicker,
  IconAction,
  ListCard,
  OffCanvas,
  PrimaryButton,
  RefreshButton,
  ScopeNote,
  SearchBox,
  Select,
  StatusBadge,
  StatusTabs,
  Td,
  TextArea,
  TextInput,
  Th,
} from "./shared/MaterialUI"

/**
 * Material Issue Note (MIN) - every internal movement of material.
 *
 * This is the screen that makes "released but not yet consumed" visible.
 * Issuing does not consume anything: it moves quantity off one holder and onto
 * another. The material is still the company's, it has just changed hands, and
 * it stays on the destination's balance until a Material Consumption Report,
 * a scrap report or a return takes it off.
 */

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "requested", label: "Requested" },
  { key: "approved", label: "Approved" },
  { key: "issued", label: "Issued" },
  { key: "received", label: "Received" },
  { key: "rejected,cancelled", label: "Closed" },
]

const TYPE_HINT: Record<string, string> = {
  issue: "Warehouse to staff or site, or staff to site. Material leaves the source but is not consumed.",
  return: "Staff or site back to a warehouse. The material came back.",
  transfer: "Between two warehouses.",
  user_to_user: "From one staff member to another. The material changes hands but is not consumed.",
}

const TYPE_LABEL: Record<string, string> = {
  issue: "Issue",
  return: "Return",
  transfer: "Transfer",
  user_to_user: "User to user",
}

interface LineDraft {
  key: string
  item: FilterOption | null
  quantity: string
  returnable: boolean
  remarks: string
}

const blankLine = (): LineDraft => ({
  key: Math.random().toString(36).slice(2),
  item: null,
  quantity: "",
  returnable: false,
  remarks: "",
})

const MaterialIssuesPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { confirm, prompt } = useConfirm()
  const canCreate = hasPermission("MaterialIssue", "create")
  const canApprove = hasPermission("MaterialIssue", "approve")
  const canIssue = hasPermission("MaterialIssue", "issue")
  const canReceive = hasPermission("MaterialIssue", "receive")
  const canCancel = hasPermission("MaterialIssue", "cancel")

  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [status, setStatus] = useState("")
  const [issueType, setIssueType] = useState("")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 400)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchPaged("/materials/issues", {
        page,
        limit,
        status: status || undefined,
        issue_type: issueType || undefined,
        project_id: project?.id,
        search: debouncedSearch.trim() || undefined,
      })
      setRows(result.rows)
      setPagination(result.pagination)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load issue notes"))
    } finally {
      setLoading(false)
    }
  }, [page, limit, status, issueType, project, debouncedSearch])

  useEffect(() => {
    load()
  }, [load])

  const act = async (row: any, action: "approve" | "reject" | "issue" | "receive" | "cancel") => {
    let body: Record<string, any> = {}

    if (action === "reject" || action === "cancel") {
      const entered = await prompt({
        title: action === "reject" ? "Reject issue note" : "Cancel issue note",
        message:
          action === "cancel" && row.status === "issued"
            ? "This note has already been issued. Cancelling reverses the movement and puts the material back with the source. If the destination has since consumed it, the reversal will be refused."
            : "The raiser will see your reason.",
        label: "Reason",
        required: true,
        multiline: true,
        confirmText: action === "reject" ? "Reject" : "Cancel note",
        cancelText: "Go back",
        variant: "warning",
      })
      if (entered === null) return
      body = { remarks: entered, reason: entered }
    }

    if (action === "issue") {
      const ok = await confirm({
        title: "Issue this material",
        message: `This moves stock off ${row.from_holder_label || "the source"} and onto ${
          row.to_holder_label || "the destination"
        }. Nothing is consumed - the material stays on the books until a consumption or scrap report accounts for it.`,
        confirmText: "Issue material",
        cancelText: "Not yet",
      })
      if (!ok) return
    }

    if (action === "receive") {
      const signoff = await prompt({
        title: "Acknowledge receipt",
        message: "Who signed for the consignment at the far end? This does not move stock - the issue already did.",
        label: "Received by (name)",
        placeholder: "Name of the person who signed",
        confirmText: "Acknowledge",
      })
      if (signoff === null) return
      body = { receiving_signoff_name: signoff }
    }

    setBusyId(row.id)
    try {
      const res = await api.post(`/materials/issues/${row.id}/${action}`, body)
      toast.success(res.data?.message || "Done")
      load()
    } catch (e: any) {
      // "Insufficient stock for item #… with warehouse #…" is a 409 and is
      // exactly the message worth showing verbatim.
      toast.error(apiError(e, `Failed to ${action} the issue note`))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <ListCard
        icon={<FaDolly className="w-5 h-5" />}
        title="Material Issue Notes (MIN)"
        subtitle="Every internal movement: warehouse to staff or site, staff to site, returns and warehouse transfers. Issuing moves stock between holders - it does not consume it."
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canCreate ? (
              <PrimaryButton onClick={() => setShowForm(true)}>
                <FaPlus className="w-4 h-4" /> New Issue Note
              </PrimaryButton>
            ) : null}
          </>
        }
        filters={
          <>
            <FilterRow>
              <FilterField label="Search" grow>
                <SearchBox
                  value={search}
                  onChange={(v) => {
                    setSearch(v)
                    setPage(1)
                  }}
                  placeholder="Note number, LR, vehicle..."
                />
              </FilterField>
              <FilterField label="Project">
                <AsyncPicker
                  value={project}
                  onChange={(o) => {
                    setProject(o)
                    setPage(1)
                  }}
                  fetcher={(s) => materialFilters.projects(s)}
                  placeholder="All my projects"
                  emptyHint="You are not assigned to any project"
                />
              </FilterField>
              <FilterField label="Movement Type">
                <Select
                  value={issueType}
                  onChange={(e) => {
                    setIssueType(e.target.value)
                    setPage(1)
                  }}
                >
                  <option value="">All movement types</option>
                  <option value="issue">Issue</option>
                  <option value="return">Return</option>
                  <option value="transfer">Transfer</option>
                  <option value="user_to_user">User to user</option>
                </Select>
              </FilterField>
            </FilterRow>
            <StatusTabs
              tabs={STATUS_TABS}
              value={status}
              onChange={(k) => {
                setStatus(k)
                setPage(1)
              }}
            />
          </>
        }
        footer={
          pagination ? (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          ) : null
        }
      >
        <DataTable
          colSpan={9}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading issue notes..."
          emptyText="No issue notes found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaDolly />}
          head={
            <>
              <Th>Actions</Th>
              <Th>Note</Th>
              <Th>Type</Th>
              <Th>From</Th>
              <Th>To</Th>
              <Th>Lines</Th>
              <Th>Raised By</Th>
              <Th>Dispatch</Th>
              <Th>Status</Th>
            </>
          }
        >
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="hover:bg-gray-50 transition-colors">
                <Cell>
                  <div className="flex items-center gap-1">
                    <ExpandAction
                      open={expanded === row.id}
                      onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                      what="items"
                    />
                    {row.status === "requested" && canApprove ? (
                      <>
                        <IconAction title="Approve" tone="green" disabled={busyId === row.id} onClick={() => act(row, "approve")}>
                          <FaCheckCircle />
                        </IconAction>
                        <IconAction title="Reject" tone="red" disabled={busyId === row.id} onClick={() => act(row, "reject")}>
                          <FaTimesCircle />
                        </IconAction>
                      </>
                    ) : null}
                    {["requested", "approved"].includes(row.status) && canIssue ? (
                      <IconAction title="Issue material" tone="indigo" disabled={busyId === row.id} onClick={() => act(row, "issue")}>
                        <FaShareSquare />
                      </IconAction>
                    ) : null}
                    {row.status === "issued" && canReceive ? (
                      <IconAction title="Acknowledge receipt" tone="teal" disabled={busyId === row.id} onClick={() => act(row, "receive")}>
                        <FaClipboardCheck />
                      </IconAction>
                    ) : null}
                    {!["received", "cancelled", "rejected"].includes(row.status) && canCancel ? (
                      <IconAction title="Cancel issue note" tone="gray" disabled={busyId === row.id} onClick={() => act(row, "cancel")}>
                        <FaBan />
                      </IconAction>
                    ) : null}
                  </div>
                </Cell>
                <Cell>
                  <div className="font-medium">{row.issue_number}</div>
                  <div className="text-xs text-gray-500">
                    {row.project?.project_short_name || row.project?.project_name || "-"}
                  </div>
                </Cell>
                <Cell>{TYPE_LABEL[row.issue_type] || row.issue_type}</Cell>
                <Cell>
                  <HolderChip type={row.from_holder_type} label={row.from_holder_label} code={row.from_holder_code} />
                </Cell>
                <Cell>
                  <HolderChip type={row.to_holder_type} label={row.to_holder_label} code={row.to_holder_code} />
                </Cell>
                <Cell>{(row.items || []).length}</Cell>
                <Cell>{fullName(row.requester)}</Cell>
                <Cell>{dateText(row.dispatch_date)}</Cell>
                <Cell>
                  <StatusBadge status={row.status} />
                </Cell>
              </tr>
              {expanded === row.id ? (
                <DetailRow colSpan={9}>
                  <IssueDetail row={row} />
                </DetailRow>
              ) : null}
            </React.Fragment>
          ))}
        </DataTable>
      </ListCard>

      {showForm ? (
        <IssueForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false)
            setPage(1)
            load()
          }}
        />
      ) : null}
    </div>
  )
}

const IssueDetail: React.FC<{ row: any }> = ({ row }) => (
  <div className="space-y-3 py-2">
    <table className="min-w-full text-sm bg-white border border-gray-200 rounded">
      <thead className="bg-gray-100">
        <tr>
          <Th>Item</Th>
          <Th>Requested</Th>
          <Th>Issued</Th>
          <Th>Received</Th>
          <Th>Returnable</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {(row.items || []).map((line: any) => (
          <tr key={line.id}>
            <Td>
              {line.item?.item_name || `#${line.item_id}`}
              {line.item?.item_code ? <span className="text-xs text-gray-400"> ({line.item.item_code})</span> : null}
            </Td>
            <Td>{qty(line.requested_quantity)}</Td>
            <Td className={num(line.issued_quantity) < num(line.requested_quantity) ? "text-amber-700" : ""}>
              {qty(line.issued_quantity)}
              {num(line.issued_quantity) > 0 && num(line.issued_quantity) < num(line.requested_quantity) ? (
                <span className="text-xs"> (short)</span>
              ) : null}
            </Td>
            <Td>{qty(line.received_quantity)}</Td>
            <Td>{line.returnable ? "Yes" : "No"}</Td>
            <Td className="text-gray-500">{line.remarks || "-"}</Td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-600">
      <div>
        <span className="font-medium text-gray-800">Transporter:</span> {row.transporter?.transporter_name || "-"}
      </div>
      <div>
        <span className="font-medium text-gray-800">LR / Vehicle:</span> {row.lr_number || "-"} / {row.vehicle_number || "-"}
      </div>
      <div>
        <span className="font-medium text-gray-800">Delivery:</span> {row.delivery_location || "-"}
      </div>
      <div>
        <span className="font-medium text-gray-800">Signed by:</span> {row.receiving_signoff_name || "-"}
      </div>
      {row.rejection_remarks ? (
        <div className="md:col-span-2 text-red-700">
          <span className="font-medium">Rejected:</span> {row.rejection_remarks}
        </div>
      ) : null}
      {row.cancel_reason ? (
        <div className="md:col-span-2 text-red-700">
          <span className="font-medium">Cancelled:</span> {row.cancel_reason}
        </div>
      ) : null}
    </div>
  </div>
)

const IssueForm: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [saving, setSaving] = useState(false)
  const [issueType, setIssueType] = useState<"issue" | "return" | "transfer" | "user_to_user">("issue")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [fromType, setFromType] = useState<HolderType>("warehouse")
  const [fromHolder, setFromHolder] = useState<FilterOption | null>(null)
  const [toType, setToType] = useState<HolderType>("site")
  const [toHolder, setToHolder] = useState<FilterOption | null>(null)
  const [transporter, setTransporter] = useState<FilterOption | null>(null)
  const [form, setForm] = useState<Record<string, string>>({
    lr_number: "",
    vehicle_number: "",
    dispatch_date: "",
    expected_delivery_date: "",
    delivery_location: "",
    remarks: "",
  })
  const [lines, setLines] = useState<LineDraft[]>([blankLine()])
  const [available, setAvailable] = useState<Record<number, number>>({})

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }))

  // The holder fetchers. Source and destination both come off the SCOPED
  // filter endpoints for staff and sites, so an option can never be offered
  // that the caller's projects could not return. Warehouses come off the
  // master, because a warehouse may legitimately be used for the first time.
  const fetchers = useMemo(
    () => ({
      warehouse: (s: string) => materialMasters.warehouses(s),
      staff: (s: string) => materialFilters.staff(project ? String(project.id) : undefined, s),
      site: (s: string) => materialFilters.sites(project ? String(project.id) : undefined, s),
    }),
    [project],
  )

  // The direction rules the server enforces, mirrored here so the form cannot
  // be built into a shape the server will refuse.
  useEffect(() => {
    if (issueType === "return") {
      setToType("warehouse")
      if (fromType === "warehouse") setFromType("site")
    } else if (issueType === "transfer") {
      setFromType("warehouse")
      setToType("warehouse")
    } else if (issueType === "user_to_user") {
      setFromType("staff")
      setToType("staff")
    } else if (toType === "warehouse") {
      setToType("site")
    }
  }, [issueType]) // eslint-disable-line react-hooks/exhaustive-deps

  // What the source actually holds, so a line that cannot be met is flagged
  // before anyone tries to issue it.
  useEffect(() => {
    if (!fromHolder || !project) {
      setAvailable({})
      return
    }
    let cancelled = false
    api
      .get("/materials/issues/availability", {
        params: { holder_type: fromType, holder_id: fromHolder.id, project_id: project.id },
      })
      .then((res) => {
        if (cancelled) return
        const map: Record<number, number> = {}
        ;(res.data?.data?.balances || []).forEach((b: any) => {
          map[Number(b.item_id)] = Number(b.available_quantity || 0)
        })
        setAvailable(map)
      })
      .catch(() => {
        if (!cancelled) setAvailable({})
      })
    return () => {
      cancelled = true
    }
  }, [fromHolder, fromType, project])

  const submit = async () => {
    if (!project) return toast.error("Pick a project")
    if (!fromHolder) return toast.error("Pick where the material is coming from")
    if (!toHolder) return toast.error("Pick where the material is going")
    if (fromType === toType && fromHolder.id === toHolder.id) {
      return toast.error("The source and destination cannot be the same holder")
    }
    const validLines = lines.filter((l) => l.item && Number(l.quantity) > 0)
    if (!validLines.length) return toast.error("Add at least one item line with a quantity")

    setSaving(true)
    try {
      const res = await api.post("/materials/issues", {
        project_id: project.id,
        issue_type: issueType,
        from_holder_type: fromType,
        from_holder_id: fromHolder.id,
        to_holder_type: toType,
        to_holder_id: toHolder.id,
        transporter_id: transporter?.id ?? null,
        ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "")),
        items: validLines.map((l) => ({
          item_id: l.item!.id,
          requested_quantity: Number(l.quantity),
          returnable: l.returnable,
          remarks: l.remarks || null,
        })),
      })
      toast.success(res.data?.message || "Issue note raised")
      onSaved()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to raise the issue note"))
    } finally {
      setSaving(false)
    }
  }

  const shortLines = lines.filter(
    (l) => l.item && Number(l.quantity) > 0 && (available[l.item.id] ?? 0) < Number(l.quantity),
  )

  return (
    <OffCanvas
      onClose={onClose}
      title="New material issue note"
      subtitle={TYPE_HINT[issueType]}
      width="xl"
      footer={
        <>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton loading={saving} onClick={submit}>
            Raise issue note
          </PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Movement type" required>
          <Select value={issueType} onChange={(e) => setIssueType(e.target.value as any)}>
            <option value="issue">Issue</option>
            <option value="return">Return to warehouse</option>
            <option value="transfer">Warehouse transfer</option>
            <option value="user_to_user">User to user</option>
          </Select>
        </Field>
        <Field label="Project" required hint="Only the projects you are assigned to">
          <AsyncPicker
            value={project}
            onChange={setProject}
            fetcher={(s) => materialFilters.projects(s)}
            placeholder="Select project..."
            emptyHint="You are not assigned to any project"
          />
        </Field>
        <Field label="Transporter" hint="Optional - when a transporter moved it">
          <AsyncPicker
            value={transporter}
            onChange={setTransporter}
            fetcher={(s) => materialMasters.transporters(s)}
            placeholder="Select transporter..."
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="From" required hint="Stock comes off this holder">
          <HolderPicker
            holderType={fromType}
            onHolderTypeChange={setFromType}
            value={fromHolder}
            onChange={setFromHolder}
            fetchers={fetchers}
            allowed={
              issueType === "transfer"
                ? ["warehouse"]
                : issueType === "user_to_user"
                  ? ["staff"]
                  : issueType === "return"
                    ? ["staff", "site"]
                    : ["warehouse", "staff"]
            }
            dependency={project?.id}
            disabled={!project}
          />
        </Field>
        <Field label="To" required hint="Stock lands on this holder">
          <HolderPicker
            holderType={toType}
            onHolderTypeChange={setToType}
            value={toHolder}
            onChange={setToHolder}
            fetchers={fetchers}
            allowed={issueType === "issue" ? ["staff", "site"] : issueType === "user_to_user" ? ["staff"] : ["warehouse"]}
            dependency={project?.id}
            disabled={!project}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Field label="Dispatch date">
          <TextInput type="date" value={form.dispatch_date} onChange={(e) => set("dispatch_date", e.target.value)} />
        </Field>
        <Field label="Expected delivery">
          <TextInput
            type="date"
            value={form.expected_delivery_date}
            onChange={(e) => set("expected_delivery_date", e.target.value)}
          />
        </Field>
        <Field label="LR number">
          <TextInput value={form.lr_number} onChange={(e) => set("lr_number", e.target.value)} />
        </Field>
        <Field label="Vehicle number">
          <TextInput value={form.vehicle_number} onChange={(e) => set("vehicle_number", e.target.value)} />
        </Field>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Items</h3>
          <GhostButton onClick={() => setLines((l) => [...l, blankLine()])}>
            <FaPlus /> Add line
          </GhostButton>
        </div>
        {shortLines.length ? (
          <div className="mb-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <FaExclamationTriangle className="mt-0.5 shrink-0" />
            <span>
              {shortLines.length} line(s) ask for more than the source holds on this project. The note can still be
              raised, but issuing will be refused until the stock is there.
            </span>
          </div>
        ) : null}
        <div className="border border-gray-200 rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th className="w-2/5">Item</Th>
                <Th>Quantity</Th>
                <Th>Available</Th>
                <Th>Returnable</Th>
                <Th>Remarks</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((line, index) => {
                const avail = line.item ? available[line.item.id] ?? 0 : null
                const short = line.item && Number(line.quantity) > 0 && (avail ?? 0) < Number(line.quantity)
                return (
                  <tr key={line.key}>
                    <Td>
                      <AsyncPicker
                        value={line.item}
                        onChange={(o) => setLines((ls) => ls.map((l, i) => (i === index ? { ...l, item: o } : l)))}
                        fetcher={(s) => materialMasters.items(s)}
                        placeholder="Select item..."
                      />
                    </Td>
                    <Td>
                      <TextInput
                        type="number"
                        min="0"
                        step="0.001"
                        value={line.quantity}
                        onChange={(e) =>
                          setLines((ls) => ls.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)))
                        }
                      />
                    </Td>
                    <Td className={short ? "text-amber-700 font-medium" : "text-gray-500"}>
                      {line.item ? qty(avail) : "-"}
                    </Td>
                    <Td>
                      <input
                        type="checkbox"
                        checked={line.returnable}
                        onChange={(e) =>
                          setLines((ls) =>
                            ls.map((l, i) => (i === index ? { ...l, returnable: e.target.checked } : l)),
                          )
                        }
                      />
                    </Td>
                    <Td>
                      <TextInput
                        value={line.remarks}
                        onChange={(e) =>
                          setLines((ls) => ls.map((l, i) => (i === index ? { ...l, remarks: e.target.value } : l)))
                        }
                      />
                    </Td>
                    <Td>
                      <button
                        onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== index) : ls))}
                        className="text-red-500 hover:text-red-700 disabled:opacity-30"
                        disabled={lines.length === 1}
                      >
                        <FaTrash />
                      </button>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">
          Mark a line returnable when the material is expected back - tools, or spares taken as cover. Returnable
          quantity still at a site shows in the In Stock head as "site returnable pending".
        </p>
      </div>

      <Field label="Delivery location">
        <TextInput value={form.delivery_location} onChange={(e) => set("delivery_location", e.target.value)} />
      </Field>
      <Field label="Remarks">
        <TextArea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
      </Field>
    </OffCanvas>
  )
}

export default MaterialIssuesPage
